import React, { useEffect, useState } from "react";
import axios from "axios";
import "./search.css";
import "./healerFrame.css";
import { Formik, Form, Field } from "formik";
import { Rating } from "react-simple-star-rating";
import { getAuth } from "firebase/auth";
import { app } from "../../firebase/firebase-config";

const auth = getAuth(app);

const HealerModal = ({
  healerState,
  setExpandedTicket,
  reviewModal,
  setReviewModal,
  onClose,
}) => {
  const [reviews, setReviews] = useState([]);
  const [avgReview, setAvgReview] = useState(0);
  const [currentUserNumericId, setCurrentUserNumericId] = useState(null);

  useEffect(() => {
    const fetchCurrentUser = () => {
      auth.onAuthStateChanged(async (user) => {
        if (user) {
          try {
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/users`);
            const users = response.data;
            const currentUser = users.find((u) => u.email.toLowerCase() === user.email.toLowerCase());
            if (currentUser) {
              setCurrentUserNumericId(currentUser.id);
            } else {
              console.error("User not found in the user list");
            }
          } catch (error) {
            console.error("Error fetching users:", error);
          }
        } else {
          setCurrentUserNumericId(null);
        }
      });
    };

    const fetchReviews = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/review/${healerState.id}`
        );
        const reviewsData = response.data;
        const reviewTotal = reviewsData.reduce(
          (acc, review) => acc + review.rating,
          0
        );
        const avgReviews =
          reviewsData.length > 0 ? reviewTotal / reviewsData.length : 0;

        setAvgReview(avgReviews);
        setReviews(reviewsData);
      } catch (error) {
        console.error("Error fetching reviews:", error);
      }
    };

    fetchCurrentUser();
    fetchReviews();
  }, [healerState.id]);

  const handleReviewSubmit = async (values, actions) => {
    try {
      if (!currentUserNumericId) {
        console.error("User not authenticated");
        return;
      }

      const payload = {
        ...values,
        rating: Math.round(values.rating), // Always send integer rating
        reviewee: healerState.id,
        reviewer: currentUserNumericId,
      };

      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/review`, payload);

      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/review/${healerState.id}`
      );
      const reviewsData = response.data;
      const reviewTotal = reviewsData.reduce(
        (acc, review) => acc + review.rating,
        0
      );
      const avgReviews =
        reviewsData.length > 0 ? reviewTotal / reviewsData.length : 0;

      setAvgReview(avgReviews);
      setReviews(reviewsData);
      actions.resetForm();
    } catch (error) {
      console.error("Error submitting review:", error);
    } finally {
      if (onClose) {
        onClose();
      } else {
        setReviewModal(false);
      }
    }
  };



  if (reviewModal) {
    return (
      <div className="healerClicked">
        <div className="singleHealer">
          <div className="reviewSelected">
            <button
              className="modal-close-button"
              onClick={onClose ? onClose : () => {
                setExpandedTicket(false);
                setReviewModal(false);
              }}
              aria-label="Close review modal"
            >
              &times;
            </button>

            <div className="healerSelectedTop">
              <p>
                {healerState.firstName} {healerState.lastName}
              </p>
            </div>

            <div className="healerSelectedMiddle">
              <p>{healerState.services}</p>

              <p style={{ fontSize: "14px", color: "#666" }}>
                {avgReview.toFixed(1)} out of 5
              </p>

              {/* Reviews Container */}
              <div
                className="reviewsContainer"
                style={{
                  marginTop: 0,
                  display: "flex",
                  flexDirection: "row",
                  gap: "8px",
                  overflowX: "auto",
                  paddingBottom: "10px",
                  scrollbarWidth: "thin",
                  scrollbarColor: "#ccc transparent",
                }}
              >
                {reviews.length > 0 ? (
                  reviews.map((review) => (
                    <div
                      className="singleReview"
                      key={review.rid}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        padding: "12px",
                        border: "1px solid #eee",
                        borderRadius: "6px",
                        fontWeight: "300",
                        fontSize: "14px",
                        color: "#444",
                        lineHeight: "1.4",
                        backgroundColor: "#fff",
                        minHeight: "100px",
                        minWidth: "220px",
                        flexShrink: 0,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          marginBottom: "8px"
                        }}
                      >
                        {/* Star rating display */}
                        <span style={{ fontSize: "18px", color: "#f39c12", marginBottom: "2px" }}>
                          {Array.from({ length: 5 }, (_, i) =>
                            i < review.rating ? "★" : "☆"
                          ).join("")}
                        </span>
                        <span style={{ fontSize: "11px", color: "#999" }}>
                          {(() => {
                            // Try createdat, createdAt, or fallback
                            const dateStr = review.createdat || review.createdAt || review.created_at;
                            return dateStr ? dateStr.slice(0, 10) : "";
                          })()}
                        </span>
                      </div>
                      <div
                        className="reviewText"
                        style={{
                          whiteSpace: "pre-wrap",
                          flexGrow: 1,
                          color: "#333",
                        }}
                      >
                        {review.comment}
                      </div>
                    </div>
                  ))
                ) : (
                  <p
                    style={{
                      fontWeight: "300",
                      color: "#999",
                      fontSize: "14px",
                    }}
                  >
                    No Reviews Yet!
                  </p>
                )}
              </div>
            </div>

            <hr />

            {/* Review form */}
            <Formik
              initialValues={{ rating: 1, comment: "" }}
              validate={values => {
                const errors = {};
                if (!values.comment || values.comment.trim() === "") {
                  errors.comment = "Comment is required";
                }
                if (!values.rating || Number(values.rating) < 1) {
                  errors.rating = "Rating is required";
                }
                return errors;
              }}
              onSubmit={handleReviewSubmit}
            >
              {({ handleSubmit, values, setFieldValue, errors, touched, isSubmitting }) => (
                <Form
                  onSubmit={handleSubmit}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <p style={{ margin: 0 }}>Rating:</p>
                    <Rating
                      onClick={(rate) => {
                        console.log('Star clicked, rate value:', rate, 'Setting rating to:', Number(rate));
                        setFieldValue("rating", Number(rate));
                      }}
                      ratingValue={values.rating * 20}
                      size={25}
                      fillColor="#f39c12"
                      emptyColor="#ddd"
                      allowHover
                    />
                    {errors.rating && touched.rating && (
                      <span style={{ color: 'red', fontSize: '12px' }}>{errors.rating}</span>
                    )}
                  </div>

                  <Field
                    name="comment"
                    as="textarea"
                    rows={3}
                    placeholder="Write your review here..."
                    style={{
                      width: "100%",
                      padding: "8px",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                      resize: "none",
                    }}
                  />
                  {errors.comment && touched.comment && (
                    <span style={{ color: 'red', fontSize: '12px' }}>{errors.comment}</span>
                  )}

                  <button
                    type="submit"
                    className="btn"
                    style={{
                      padding: "8px 16px",
                      cursor: Number(values.rating) < 1 || !values.comment.trim() ? "not-allowed" : "pointer",
                      alignSelf: "flex-start",
                      opacity: Number(values.rating) < 1 || !values.comment.trim() ? 0.5 : 1,
                    }}
                    disabled={Number(values.rating) < 1 || !values.comment.trim() || isSubmitting}
                  >
                    Submit
                  </button>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default HealerModal;
